/* design-rules: allow-hex — logo artwork colors are fixed by the brand book (not theme tokens). */
import type { CSSProperties } from 'react';

// Geometry copied 1:1 from design-system/assets/Logos (logo-horizontal-*, symbol-*, app-icon-*). Do not redraw.
const S_PATH = 'M44 14H29A9 9 0 0 0 29 32H35A9 9 0 0 1 35 50H20';
const WORDMARK = [
  { t: 'translate(60 2.5)', d: 'M25.5 2.5H11A8.5 8.5 0 0 0 11 19.5H17A8.5 8.5 0 0 1 17 36.5H2.5' },
  { t: 'translate(97 2.5)', d: 'M25.5 2.5H2.5V36.5H25.5M2.5 19.5H20' },
  { t: 'translate(134 2.5)', d: 'M2.5 36.5V2.5H15A8.5 8.5 0 0 1 15 19.5H2.5M14 19.5L25.5 36.5' },
  {
    t: 'translate(171 2.5)',
    d: 'M11 2.5H17A8.5 8.5 0 0 1 25.5 11V28A8.5 8.5 0 0 1 17 36.5H11A8.5 8.5 0 0 1 2.5 28V11A8.5 8.5 0 0 1 11 2.5Z',
  },
  { t: 'translate(208 2.5)', d: 'M2.5 2.5H25.5M14 2.5V36.5' },
  { t: 'translate(245 2.5)', d: 'M2.5 36.5V11A8.5 8.5 0 0 1 11 2.5H17A8.5 8.5 0 0 1 25.5 11V36.5M2.5 22H25.5' },
];

// Brand constants of the logo artwork (fixed by the brand book; not theme tokens).
const ORANGE = 'var(--logo-orange, #FF5B2E)';
const BLACK = 'var(--logo-black, #111111)';
const WHITE = 'var(--logo-white, #FFFFFF)';

export interface LogoProps {
  /** light = açık zemin (kelime işareti ink), dark = koyu zemin (beyaz), mono-black / mono-white = tek renk, auto = tema metin rengi */
  variant?: 'auto' | 'light' | 'dark' | 'mono-black' | 'mono-white';
  height?: number;
  title?: string;
  style?: CSSProperties;
  className?: string;
}

/** Yatay logo (S-rota simgesi + geometrik kelime işareti). Minimum genişlik 96px; altında Symbol kullan. */
export function Logo({ variant = 'auto', height = 24, title = 'SEROTA', style, className }: LogoProps) {
  const word = variant === 'auto' ? 'currentColor' : variant === 'dark' || variant === 'mono-white' ? WHITE : BLACK;
  const tile = variant === 'mono-black' ? BLACK : variant === 'mono-white' ? WHITE : ORANGE;
  const sStroke = variant === 'mono-black' ? WHITE : variant === 'mono-white' ? BLACK : BLACK;
  const startDot = variant === 'mono-white' ? BLACK : WHITE;
  const endFill = variant === 'mono-white' ? WHITE : BLACK;
  const endStroke = variant === 'mono-black' ? WHITE : variant === 'mono-white' ? BLACK : BLACK;
  const oDot = variant.startsWith('mono') ? word : ORANGE;
  return (
    <svg
      viewBox="0 0 273 44"
      height={height}
      role="img"
      aria-label={title}
      style={{ color: 'var(--ink)', ...style }}
      className={className}
    >
      <rect width="44" height="44" rx="9.62" fill={tile} />
      <g transform="scale(0.6875)">
        <path d={S_PATH} fill="none" stroke={sStroke} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="44" cy="14" r="6.5" fill={startDot} />
        <circle cx="20" cy="50" r="5" fill={endFill} stroke={endStroke} strokeWidth="3.5" />
      </g>
      <g
        className="sr-side__wordmark"
        fill="none"
        stroke={word}
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {WORDMARK.map((w) => (
          <path key={w.t} transform={w.t} d={w.d} />
        ))}
      </g>
      <circle className="sr-side__wordmark" cx="185" cy="22" r="4" fill={oDot} />
    </svg>
  );
}

/** Simge: favicon, avatar, dar alan. */
export function LogoSymbol({ size = 32, dark, title = 'SEROTA' }: { size?: number; dark?: boolean; title?: string }) {
  const stroke = dark ? WHITE : BLACK;
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} role="img" aria-label={title}>
      <path d={S_PATH} fill="none" stroke={stroke} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="44" cy="14" r="6.5" fill={ORANGE} />
      <circle cx="20" cy="50" r="5" fill={dark ? BLACK : WHITE} stroke={stroke} strokeWidth="3.5" />
    </svg>
  );
}

/** Uygulama ikonu: passenger = turuncu, driver = siyah + turuncu S, dark = beyaz etiket tabanı. */
export function AppIcon({ app = 'passenger', size = 64 }: { app?: 'passenger' | 'driver' | 'dark'; size?: number }) {
  const bg = app === 'passenger' ? ORANGE : BLACK;
  const s = app === 'passenger' ? BLACK : app === 'driver' ? ORANGE : WHITE;
  const start = app === 'dark' ? ORANGE : WHITE;
  return (
    <svg
      viewBox="0 0 1024 1024"
      width={size}
      height={size}
      role="img"
      aria-label={`SEROTA ${app === 'driver' ? 'Şoför' : 'Yolcu'} uygulama ikonu`}
    >
      <rect width="1024" height="1024" rx={app === 'passenger' ? 230 : 224} fill={bg} />
      <g transform="scale(16)">
        <path d={S_PATH} fill="none" stroke={s} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="44" cy="14" r="6.5" fill={start} />
        <circle cx="20" cy="50" r="5" fill={BLACK} stroke={s} strokeWidth="3.5" />
      </g>
    </svg>
  );
}
